import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import * as luxon from 'luxon'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import { ChartContainer } from '@/components/ui/chart'
import { useMemo } from 'react'
import { store } from '@/lib/states/store'
import { ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { trpc } from '@/lib/trpc/client'

export const Route = createFileRoute('/_auth/_dashboard/dashboard')({
  beforeLoad: () => {
    throw redirect({ to: '/clients', replace: true })
  },
  component: DashboardPage,
  loader: () => {
    const org = store.getState().session.org
    if (!org) {
      throw new Error('Org not found')
    }
    return { org }
  },
})

function DashboardPage() {
  const { org } = Route.useLoaderData()

  const { data: dashboard } = trpc.org.dashboard.useQuery(undefined, {
    enabled: !!org,
  })
  const { data: pendingCheckinsCountData } = trpc.checkins.waitingCount.useQuery(
    undefined,
    { enabled: !!org },
  )

  const pendingCheckIns = pendingCheckinsCountData ?? 0
  const reviewedThisWeek = dashboard?.reviewedLast7Days ?? 0
  const reviewRate = useMemo(() => {
    const total = reviewedThisWeek + pendingCheckIns
    if (!total) return 0
    return Math.round((reviewedThisWeek / total) * 100)
  }, [pendingCheckIns, reviewedThisWeek])

  const purchasesData = dashboard?.purchases ?? []
  const newClientsData = dashboard?.newClients ?? []
  const subscriptionsData = dashboard?.subscriptions ?? []

  const revenueCurrency =
    purchasesData.find((purchase) => purchase.currency)?.currency ??
    subscriptionsData.find((subscription) => subscription.currency)?.currency ??
    'USD'

  const revenueTotalCents = purchasesData.reduce(
    (sum, purchase) => sum + (purchase.price ?? 0),
    0,
  )

  const newClientsCount = newClientsData.length
  const mrrCents = subscriptionsData.reduce((sum, sub) => {
    const price = sub.price ?? 0
    if (!price) return sum
    const planType = sub.plan_type?.toLowerCase() ?? ''
    if (planType.includes('week')) {
      return sum + Math.round(price * (52 / 12))
    }
    if (planType.includes('month')) {
      return sum + price
    }
    return sum
  }, 0)

  const financialSeries = useMemo(() => {
    const start = luxon.DateTime.utc().startOf('day').minus({ days: 6 })
    const days = Array.from({ length: 7 }).map((_, i) =>
      start.plus({ days: i }),
    )

    const revenueByDay = new Map<string, number>()
    const clientsByDay = new Map<string, number>()

    purchasesData.forEach((purchase) => {
      const dayKey = luxon.DateTime.fromISO(purchase.created_at)
        .startOf('day')
        .toISODate()
      revenueByDay.set(
        dayKey ?? '',
        (revenueByDay.get(dayKey ?? '') ?? 0) + (purchase.price ?? 0),
      )
    })

    newClientsData.forEach((client) => {
      const dayKey = luxon.DateTime.fromISO(client.created_at)
        .startOf('day')
        .toISODate()
      clientsByDay.set(dayKey ?? '', (clientsByDay.get(dayKey ?? '') ?? 0) + 1)
    })

    return days.map((day) => {
      const key = day.toISODate() ?? ''
      return {
        date: day.toFormat('MMM d'),
        revenue: revenueByDay.get(key) ?? 0,
        clients: clientsByDay.get(key) ?? 0,
      }
    })
  }, [newClientsData, purchasesData])

  return (
    <>
      <div className="w-full overflow-x-hidden">
        <div className="flex-col md:flex">
          <div className="flex-1 space-y-4 p-6 md:p-8 pt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                Dashboard
              </h2>
            </div>
            {/* Stat Tiles (A) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 order-2 md:order-1">
              <StatTile
                label="Total Clients"
                value={dashboard?.clientCount ?? 0}
                to="/clients"
              />
              <StatTile
                label="Pending Check-Ins"
                value={pendingCheckIns}
                tone="amber"
                to="/checkins"
              />
              <StatTile
                label="Reviewed (7d)"
                value={reviewedThisWeek}
                tone="primary"
              />
              <StatTile label="Review Rate" value={`${reviewRate}%`} />
            </div>

            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Financial Overview</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile
                label="New Clients (7D)"
                value={newClientsCount}
                tone="primary"
              />
              <StatTile
                label="Revenue (30d)"
                value={formatCurrency(revenueTotalCents, revenueCurrency)}
              />
              <StatTile label="Active Subs" value={subscriptionsData.length} />
              <StatTile
                label="MRR"
                value={formatCurrency(mrrCents, revenueCurrency)}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2 grid-cols-1">
              <Card className="min-w-0">
                <CardHeader className="pb-2">
                  <CardTitle>Revenue</CardTitle>
                  <CardDescription>Daily total this week</CardDescription>
                </CardHeader>
                <CardContent className="pt-2">
                  <ChartContainer
                    config={{
                      revenue: {
                        label: 'Revenue',
                        color: 'hsl(var(--primary))',
                      },
                    }}
                    className="h-40 w-full"
                  >
                    <AreaChart data={financialSeries}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="date" tickLine={false} axisLine={false} />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        width={48}
                        tickFormatter={(value) =>
                          formatCurrency(Number(value), revenueCurrency)
                        }
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(value) =>
                              formatCurrency(Number(value), revenueCurrency)
                            }
                          />
                        }
                      />
                      <Area
                        dataKey="revenue"
                        type="monotone"
                        stroke="var(--color-revenue)"
                        fill="var(--color-revenue)"
                        fillOpacity={0.2}
                      />
                    </AreaChart>
                  </ChartContainer>
                </CardContent>
              </Card>

              <Card className="min-w-0">
                <CardHeader className="pb-2">
                  <CardTitle>New Clients</CardTitle>
                  <CardDescription>
                    New client signups this week
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-2">
                  <ChartContainer
                    config={{
                      clients: {
                        label: 'Clients',
                        color: 'hsl(var(--primary))',
                      },
                    }}
                    className="h-40 w-full"
                  >
                    <AreaChart data={financialSeries}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="date" tickLine={false} axisLine={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Area
                        dataKey="clients"
                        type="monotone"
                        stroke="var(--color-clients)"
                        fill="var(--color-clients)"
                        fillOpacity={0.2}
                      />
                    </AreaChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function formatCurrency(amountCents: number, currency?: string) {
  if (!Number.isFinite(amountCents)) return '-'
  const amount = amountCents / 100
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'USD',
      maximumFractionDigits: 0,
      currencyDisplay: 'narrowSymbol',
    }).format(amount)
  } catch {
    return `${currency ?? ''} ${amount.toFixed(2)}`
  }
}

function StatTile({
  label,
  value,
  tone,
  to,
}: {
  label: string
  value: any
  tone?: 'amber' | 'primary'
  to?: string
}) {
  const toneClasses =
    tone === 'amber'
      ? 'text-amber-600'
      : tone === 'primary'
        ? 'text-primary'
        : 'text-foreground'
  const content = (
    <>
      <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
        {label}
      </span>
      <span
        className={`text-lg md:text-xl font-semibold leading-none ${toneClasses}`}
      >
        {value}
      </span>
    </>
  )

  if (to) {
    return (
      <Link
        to={to}
        className="rounded-md border bg-card px-4 py-3 flex flex-col gap-1 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors hover:bg-accent/40 cursor-pointer"
        aria-label={`${label} – view details`}
      >
        {content}
      </Link>
    )
  }
  return (
    <div className="rounded-md border bg-card px-4 py-3 flex flex-col gap-1 shadow-sm">
      {content}
    </div>
  )
}
